import React, { useEffect, useState } from 'react';
import {
  withStyles,
  WithStyles,
  Theme,
  createStyles,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
} from '@material-ui/core';

export const OTP_LENGTH = 6;
// Wait before another resend, so quick clicks can't use up the day's OTPs.
const RESEND_COOLDOWN_S = 60;

const CHANNEL_NAMES: Record<string, string> = { whatsapp: 'WhatsApp', sms: 'SMS', email: 'email' };

const channelText = (channels: string[]): string => {
  const names = ['whatsapp', 'sms', 'email'].filter((c) => channels.includes(c)).map((c) => CHANNEL_NAMES[c]);
  if (names.length <= 1) {
    return names[0] || 'WhatsApp';
  }
  return `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`;
};

const OtpForm = (props: Props): React.ReactElement => {
  const {
    classes,
    phoneLabel,
    otpChannels,
    otp,
    onOtpChange,
    onSubmit,
    onResend,
    onEditDetails,
    isSubmitting,
    isResending,
    error,
  } = props;

  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_S);

  useEffect(() => {
    if (cooldown <= 0) {
      return undefined;
    }
    const timer = window.setTimeout(() => setCooldown((left) => left - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);

  const handleResend = () => {
    setCooldown(RESEND_COOLDOWN_S);
    onResend();
  };

  const handleOtp = (e: React.ChangeEvent<HTMLInputElement>) => {
    onOtpChange(e.target.value.replace(/\D/g, '').slice(0, OTP_LENGTH));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && otp.length === OTP_LENGTH && !isSubmitting) {
      onSubmit();
    }
  };

  return (
    <Box className={classes.root}>
      <Typography variant="body1" gutterBottom>
        Enter the 6-digit OTP sent to your {channelText(otpChannels)}
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Sent to {phoneLabel}.{' '}
        <Button
          size="small"
          color="primary"
          onClick={onEditDetails}
          disabled={isSubmitting}
        >
          Edit number
        </Button>
      </Typography>
      <TextField
        fullWidth
        autoFocus
        placeholder="------"
        value={otp}
        onChange={handleOtp}
        onKeyDown={handleKeyDown}
        inputProps={{
          inputMode: 'numeric',
          autoComplete: 'one-time-code',
          maxLength: OTP_LENGTH,
          className: classes.otpInput,
        }}
      />

      <Box pt={3}>
        {error ? (
          <Typography variant="body2" color="error" gutterBottom>
            {error}
          </Typography>
        ) : null}
        <Button
          fullWidth
          variant={isSubmitting ? 'outlined' : 'contained'}
          color="primary"
          onClick={onSubmit}
          disabled={isSubmitting || otp.length !== OTP_LENGTH}
          className={classes.submit}
        >
          {isSubmitting ? <CircularProgress size={20} /> : 'Verify'}
        </Button>
        <Box pt={1} textAlign="center">
          <Button
            size="small"
            color="primary"
            onClick={handleResend}
            disabled={isSubmitting || isResending || cooldown > 0}
          >
            {isResending ? (
              <CircularProgress size={16} />
            ) : cooldown > 0 ? (
              `Resend OTP in ${cooldown}s`
            ) : (
              'Resend OTP'
            )}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

interface Props extends WithStyles<typeof styles> {
  phoneLabel: string;
  otpChannels: string[];
  otp: string;
  onOtpChange: (otp: string) => void;
  onSubmit: () => void;
  onResend: () => void;
  onEditDetails: () => void;
  isSubmitting: boolean;
  isResending: boolean;
  error: string;
}

const styles = (theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
    },
    otpInput: {
      letterSpacing: theme.spacing(1),
      textAlign: 'center',
      fontSize: theme.typography.h3.fontSize,
    },
    submit: {
      color: theme.palette.common.white,
    },
  });

export default withStyles(styles)(OtpForm);
